import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, ExternalLink, Zap, Star, Crown, Users } from 'lucide-react';
import { usePrixPlans, formatFcfa } from '../lib/tarifs';

const PLATFORM_URL = 'https://app.innovactive360.cloud';

const PLAN_DETAILS = {
  gratuit: {
    label: 'Plan Gratuit',
    color: 'text-gray-500',
    icon: Users,
    moocAccess: 'Cours gratuits uniquement',
    badge: null,
  },
  pro: {
    label: 'Plan Pro',
    color: 'text-blue-600',
    icon: Star,
    moocAccess: 'Tous les cours & certifications',
    badge: 'Populaire',
  },
  promax: {
    label: 'Plan Pro Max',
    color: 'text-purple-600',
    icon: Crown,
    moocAccess: 'Tous les cours & certifications',
    badge: 'Pro Max',
  },
  'boost-ia': {
    label: 'Boost IA',
    color: 'text-orange-500',
    icon: Zap,
    moocAccess: 'Tous les cours & certifications + IA',
    badge: 'IA',
  },
};

const Subscription = () => {
  const prix = usePrixPlans();
  const prixAnnuel = (plan) => (prix?.[plan]?.annual ? ` — ${formatFcfa(prix[plan].annual)} par an` : '');
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/subscriptions/current')
      .then(r => setCurrentSub(r.data))
      .catch(() => setCurrentSub({ planId: 'gratuit', planName: 'Plan Gratuit' }))
      .finally(() => setLoading(false));
  }, []);

  const planInfo = PLAN_DETAILS[currentSub?.planId] || PLAN_DETAILS.gratuit;
  const PlanIcon = planInfo.icon;
  const isPaid = currentSub?.planId && currentSub.planId !== 'gratuit';

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* En-tête */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Mon abonnement</h1>
          <p className="text-gray-500">
            Les abonnements sont gérés depuis la plateforme principale Innovactive360°.
          </p>
        </div>

        {/* Plan actuel */}
        <div className={`bg-white rounded-2xl border-2 p-8 mb-6 shadow-sm ${
          isPaid ? 'border-blue-200' : 'border-gray-200'
        }`}>
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  isPaid ? 'bg-blue-50' : 'bg-gray-100'
                }`}>
                  <PlanIcon className={`w-7 h-7 ${planInfo.color}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Plan actuel</p>
                  <p className={`text-2xl font-bold ${planInfo.color}`}>{currentSub?.planName || planInfo.label}</p>
                </div>
                {isPaid && (
                  <span className="ml-auto bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                    Actif
                  </span>
                )}
              </div>

              {/* Accès MOOC */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Votre accès sur le MOOC
                </p>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{planInfo.moocAccess}</span>
                </div>
                {!isPaid && (
                  <p className="mt-3 text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                    Passez au plan Pro ou Pro Max pour accéder à tous les cours et certifications.
                  </p>
                )}
                {currentSub?.renewalDate && (
                  <p className="mt-3 text-xs text-gray-400">
                    Renouvellement le {new Date(currentSub.renewalDate).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>

              {/* Bouton vers plateforme unifiée */}
              <a
                href={`${PLATFORM_URL}/subscription`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
              >
                {isPaid ? 'Gérer mon abonnement' : 'Choisir un plan'}
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-center text-xs text-gray-400 mt-3">
                Vous serez redirigé vers <strong>app.innovactive360.cloud</strong>
              </p>
            </>
          )}
        </div>

        {/* Rappel des plans */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="font-semibold text-gray-800 mb-4">Accès MOOC selon votre plan</h2>
          <div className="space-y-3">
            {[
              { id: 'gratuit', label: 'Plan Gratuit', access: 'Cours gratuits uniquement', included: false },
              { id: 'pro', label: `Plan Pro${prixAnnuel('pro')}`, access: 'Tous les cours & certifications', included: true },
              { id: 'promax', label: `Plan Pro Max${prixAnnuel('promax')}`, access: 'Tous les cours & certifications', included: true },
            ].map(row => (
              <div key={row.id} className={`flex items-center gap-3 p-3 rounded-lg ${
                currentSub?.planId === row.id ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
              }`}>
                {row.included
                  ? <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  : <span className="w-4 h-4 flex-shrink-0 text-gray-300 text-center leading-none">✕</span>
                }
                <div className="flex-1">
                  <span className="font-medium text-gray-800 text-sm">{row.label}</span>
                  <span className="text-gray-500 text-sm"> — {row.access}</span>
                </div>
                {currentSub?.planId === row.id && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Actuel</span>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Subscription;
